import { useMemo } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Bell, Link2 } from "lucide-react";
import { toast } from "sonner";
import { invoiceBadgeClass, invoiceStatusLabel } from "../../lib/badges";
import { StatusEditor } from "./StatusEditor";

const sendReminder = async (id) => {
  try {
    const r = await api.post(`/rent-invoices/${id}/send-reminder`);
    const map = { sent: "Email sent", mocked: "Reminder logged (set RESEND_API_KEY to send for real)", error: "Send failed" };
    toast.success(map[r.data.status] || "Reminder triggered");
  } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
};

const createPaymentLink = async (id) => {
  try {
    const r = await api.post(`/rent-invoices/${id}/payment-link`);
    window.prompt(r.data.status === "mocked"
      ? "Mock payment link (Razorpay keys not set):"
      : "Razorpay payment link — copy & share:", r.data.short_url);
  } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
};

function InvoiceRow({ inv, name, today, onSaved }) {
  const status = inv.status === "pending" && inv.due_date < today ? "overdue" : inv.status;
  return (
    <tr className="border-b border-slate-100 hover:bg-slate-50">
      <td className="px-6 py-4 font-medium">{name}</td>
      <td className="px-6 py-4 font-mono text-sm">{inv.period}</td>
      <td className="px-6 py-4 font-mono">₹{Number(inv.amount).toLocaleString("en-IN")}</td>
      <td className="px-6 py-4 text-sm">{inv.due_date}</td>
      <td className="px-6 py-4">
        <span className={invoiceBadgeClass(status)} data-testid={`status-${inv.id}`}>{invoiceStatusLabel(inv, today)}</span>
      </td>
      <td className="px-6 py-4 text-right space-x-1">
        <StatusEditor inv={inv} onSaved={onSaved} />
        {inv.status !== "paid" && (
          <>
            <button onClick={() => sendReminder(inv.id)} title="Email reminder" data-testid={`remind-${inv.id}`}
                    className="text-blue-700 hover:bg-blue-50 p-2"><Bell className="h-4 w-4" /></button>
            <button onClick={() => createPaymentLink(inv.id)} title="Razorpay link" data-testid={`paylink-${inv.id}`}
                    className="text-purple-700 hover:bg-purple-50 p-2"><Link2 className="h-4 w-4" /></button>
          </>
        )}
      </td>
    </tr>
  );
}

export function InvoiceTable({ items, tenants, onSaved }) {
  const names = useMemo(() => Object.fromEntries(tenants.map((t) => [t.id, t.name])), [tenants]);
  const today = new Date().toISOString().slice(0, 10);
  if (items.length === 0) {
    return <div className="p-12 text-center text-slate-500" data-testid="invoices-empty">No invoices yet.</div>;
  }
  return (
    <table className="w-full">
      <thead className="bg-slate-50">
        <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
          <th className="px-6 py-3">Tenant</th><th className="px-6 py-3">Period</th>
          <th className="px-6 py-3">Amount</th><th className="px-6 py-3">Due</th>
          <th className="px-6 py-3">Status</th><th className="px-6 py-3"></th>
        </tr>
      </thead>
      <tbody>
        {items.map((i) => (
          <InvoiceRow key={i.id} inv={i} name={names[i.tenant_id] || "—"} today={today} onSaved={onSaved} />
        ))}
      </tbody>
    </table>
  );
}
